/* eslint-disable react-hooks/exhaustive-deps */
import { InfoCircleFilled } from "@ant-design/icons";
import {
  Table,
  Button,
  Typography,
  Select,
  DatePicker,
  Row,
  Col,
  Card,
  Statistic,
  Input
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState, useRef } from "react";
import { CSVLink } from "react-csv";

import { showErrorNotification, showSuccessNotification } from "../../utils/notification";
import { clamp } from "../../utils/responsiveHelpers";
import AnalyticsGenericBarGraph from "../common-utils/GenericBarGraph";
import TooltipWrapper from "../common-utils/TooltipWrapper";

const { Option } = Select;
const { Text, Title } = Typography;

const CourseAnalyticsDash = ({ ROOT_URL }) => {
  const BASE_BACKEND_URL = "admin/courses/manage/analytics/general";

  const currentDate = new Date();
  const defaultPurchasedStartDate = new Date("2025-04-19T00:00:00");
  const defaultActivityStartDate = new Date("2025-06-30T00:00:00");
  const csvLinkRef = useRef(null);

  // Filter States.
  const [filter, setFilter] = useState({
    purchaseStartDate: defaultPurchasedStartDate,
    purchaseEndDate: currentDate,
    totalUserCountStartDate: defaultPurchasedStartDate,
    totalUserCountEndDate: currentDate,
    startDate: defaultActivityStartDate,
    endDate: currentDate,
    selectedCourse: "/learnings/Self-Paced-Level-1", // Default course selection
    selectedCourseId: "67e31eacff107aa398964a77" // Default course ID for "Self-Paced Level 1"
  });

  // Page states.
  const [coursesList, setCoursesList] = useState([]);
  const [courseStats, setCourseStats] = useState({});
  const [newUsersCount, setNewUsersCount] = useState(0);
  const [codeforcesVerifiedFilter, setCodeforcesVerifiedFilter] = useState("all");
  const [discordConnectedFilter, setDiscordConnectedFilter] = useState("all");
  const [dailyUniqueVisitCountData, setDailyUniqueVisitCountData] = useState([]);
  const [daliyProblemsSolvedData, setDailyProblemsSolvedData] = useState([]);
  const [dailyLecturesMarkedData, setDailyLecturesMarkedData] = useState([]);
  const [dailyDoubtsCreatedData, setDailyDoubtsCreatedData] = useState([]);
  const [userActivityTableData, setUserActivityTableData] = useState([]);
  const [courseRemindButtonCsvData, setCourseRemindButtonCsvData] = useState([]);
  const [downloadedFileName, setDownloadedFileName] = useState("RemindUsData.csv");
  const [aggregatedStats, setAggregatedStats] = useState({});
  const [daysSinceLastVisitData, setDaysSinceLastVisitData] = useState([]);
  const [courseProgressData, setCourseProgressData] = useState([]);
  const [filterUserEmail, setFilterUserEmail] = useState("");

  const handleCoursesChange = (course) => {
    setFilter((prev) => ({
      ...prev,
      selectedCourse: `/learnings/${course.value}`,
      selectedCourseId: course.id
    }));
  };

  const fetchFilterDataRequisites = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/filter-requisites`, {})
      .then((res) => {
        const { courses = {} } = res.data;
        setCoursesList(courses);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchCourseStats = () => {
    axios
      .post(`${ROOT_URL}/${BASE_BACKEND_URL}/course-stats`, {
        courses: coursesList,
        purchaseStartDate: filter.purchaseStartDate,
        purchaseEndDate: filter.purchaseEndDate
      })
      .then((res) => {
        setCourseStats(res.data);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchNewUsersCount = () => {
    axios
      .post(`${ROOT_URL}/${BASE_BACKEND_URL}/new-users`, {
        filter: {
          startDate: filter.totalUserCountStartDate,
          endDate: filter.totalUserCountEndDate
        }
      })
      .then((res) => {
        setNewUsersCount(res.data.totalNewUsers || 0);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const downloadPlainTextCSV = (data, filename) => {
    const csvContent = data.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadNewUsersCSV = () => {
    const startDateStr = dayjs(filter.totalUserCountStartDate).format("YYYY-MM-DD");
    const endDateStr = dayjs(filter.totalUserCountEndDate).format("YYYY-MM-DD");
    axios
      .post(`${ROOT_URL}/${BASE_BACKEND_URL}/export-new-users-csv`, {
        filter: {
          startDate: filter.totalUserCountStartDate,
          endDate: filter.totalUserCountEndDate
        }
      })
      .then((response) => {
        const { newUserEmails } = response.data;
        downloadPlainTextCSV(newUserEmails, `new_users_${startDateStr}_to_${endDateStr}.csv`);
        showSuccessNotification("New users CSV downloaded successfully");
      })
      .catch(() => {
        showErrorNotification("Error downloading New users CSV");
      });
  };

  const handleDownloadCodeforcesUsersCSV = (filterValue) => {
    axios
      .post(`${ROOT_URL}/${BASE_BACKEND_URL}/export-codeforces-verified-users-csv`, {
        courses: coursesList,
        codeforcesVerifiedFilter: filterValue,
        purchaseStartDate: filter.purchaseStartDate,
        purchaseEndDate: filter.purchaseEndDate
      })
      .then((response) => {
        const { csvData } = response.data;
        downloadPlainTextCSV(
          csvData,
          `course_analytics_codeforces_verified_users_${new Date().toISOString().split("T")[0]}.csv`
        );
        showSuccessNotification("Codeforces users CSV downloaded successfully");
      })
      .catch(() => {
        showErrorNotification("Error downloading Codeforces users CSV");
      });
  };

  const handleDownloadDiscordUsersCSV = (filterValue) => {
    axios
      .post(`${ROOT_URL}/${BASE_BACKEND_URL}/export-discord-connected-users-csv`, {
        courses: coursesList,
        discordConnectedFilter: filterValue,
        purchaseStartDate: filter.purchaseStartDate,
        purchaseEndDate: filter.purchaseEndDate
      })
      .then((response) => {
        const { csvData } = response.data;
        downloadPlainTextCSV(
          csvData,
          `course_analytics_discord_connected_users_${new Date().toISOString().split("T")[0]}.csv`
        );
        showSuccessNotification("Discord users CSV downloaded successfully");
      })
      .catch(() => {
        showErrorNotification("Error downloading Discord users CSV");
      });
  };

  const fetchUserActivityData = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/user-activity-data`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const {
          lecturesMarkedData = [],
          problemsMarkedData = [],
          doubtCreatedData = [],
          dailyVisitCountData = []
        } = res.data;
        setDailyProblemsSolvedData(problemsMarkedData);
        setDailyLecturesMarkedData(lecturesMarkedData);
        setDailyDoubtsCreatedData(doubtCreatedData);
        setDailyUniqueVisitCountData(dailyVisitCountData);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchUserActivityTableData = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/user-activity-table-data`, {
        params: {
          filter: {
            ...filter,
            userEmail: filterUserEmail
          }
        }
      })
      .then((res) => {
        const { tableData = [], aggregatedStats = {} } = res.data;
        setUserActivityTableData(tableData);
        setAggregatedStats(aggregatedStats);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchDaysSinceLastVisitData = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/last-visit-days`, {
        params: {
          filter
        }
      })
      .then((res) => {
        setDaysSinceLastVisitData(res.data || []);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchCourseProgressData = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/course-progress`, {
        params: {
          filter
        }
      })
      .then((res) => {
        setCourseProgressData(res.data || []);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const infoComponentForStatsCalc = (info) => {
    return (
      <TooltipWrapper
        title={`Students who ${info} >= 1 / Total Users`}
        body={<InfoCircleFilled />}
      />
    );
  };

  const columns = [
    {
      title: "S No.",
      dataIndex: "index",
      key: "index"
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email"
    },
    {
      title: "Purchased Date",
      dataIndex: "purchasedDate",
      key: "purchasedDate"
    },
    {
      title: "Problem Solved",
      dataIndex: "totalProblemsMarked",
      key: "totalProblemsMarked"
    },
    {
      title: "Lectures Marked",
      dataIndex: "totalLecturesMarked",
      key: "totalLecturesMarked"
    },
    {
      title: "Doubts Asked",
      dataIndex: "totalDoubtsCreated",
      key: "totalDoubtsCreated"
    },
    {
      title: "Total Visits",
      dataIndex: "totalVisitCount",
      key: "totalVisitCount"
    },
    {
      title: "Doubt Support Active",
      dataIndex: "isDoubtSupportActive",
      key: "isDoubtSupportActive"
    },
    {
      title: "Discord Expiry Date",
      dataIndex: "discordExpiryDate",
      key: "discordExpiryDate"
    }
  ];

  const handleDownloadCsv = () => {
    setDownloadedFileName(`${filter.selectedCourse}-UserActivityData.csv`);
    setCourseRemindButtonCsvData(userActivityTableData);
    showSuccessNotification("CSV downloaded successfully");
  };

  useEffect(() => {
    if (courseRemindButtonCsvData.length > 0 && csvLinkRef.current) {
      csvLinkRef.current.link.click();
    }
  }, [courseRemindButtonCsvData]);

  useEffect(() => {
    fetchFilterDataRequisites();
  }, []);

  useEffect(() => {
    if (filter.totalUserCountStartDate && filter.totalUserCountEndDate) {
      fetchNewUsersCount();
    }
  }, [filter.totalUserCountStartDate, filter.totalUserCountEndDate]);

  useEffect(() => {
    if (
      filter?.purchaseStartDate &&
      filter?.purchaseEndDate &&
      filter?.startDate &&
      filter?.endDate
    ) {
      fetchUserActivityData();
      fetchUserActivityTableData();
      fetchDaysSinceLastVisitData();
      fetchCourseProgressData();
      fetchCourseStats();
    }
  }, [filter, coursesList]);

  return (
    <div
      style={{
        marginTop: "20px",
        marginLeft: clamp(12, 30),
        marginRight: clamp(12, 30)
      }}
    >
      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: "24px",
          flexWrap: "wrap"
        }}
        justify="start"
        align="stretch"
      >
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Card style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Statistic title="New users" value={newUsersCount} style={{ marginBottom: "16px" }} />
            <div style={{ marginTop: "auto", display: "flex", gap: "8px" }}>
              <DatePicker.RangePicker
                style={{ flex: 1 }}
                value={
                  filter.totalUserCountStartDate && filter.totalUserCountEndDate
                    ? [dayjs(filter.totalUserCountStartDate), dayjs(filter.totalUserCountEndDate)]
                    : []
                }
                onChange={(dates) => {
                  if (dates) {
                    setFilter((prevFilter) => ({
                      ...prevFilter,
                      totalUserCountStartDate: dates[0].clone().startOf("day").toDate(),
                      totalUserCountEndDate: dates[1].clone().endOf("day").toDate()
                    }));
                  } else {
                    setFilter((prevFilter) => ({
                      ...prevFilter,
                      totalUserCountStartDate: null,
                      totalUserCountEndDate: null
                    }));
                  }
                }}
                size="small"
              />
              <Button type="primary" size="small" onClick={() => handleDownloadNewUsersCSV()}>
                Download CSV
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={24} md={12} lg={8}>
          <Text strong>Purchased Date filter:</Text>
          <DatePicker.RangePicker
            style={{ width: "100%", marginTop: "8px" }}
            value={
              filter.purchaseStartDate && filter.purchaseEndDate
                ? [dayjs(filter.purchaseStartDate), dayjs(filter.purchaseEndDate)]
                : []
            }
            disabledDate={(current) => current && current < dayjs("2025-04-19").startOf("day")}
            onChange={(dates) => {
              if (dates) {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  purchaseStartDate: dates[0].clone().startOf("day").toDate(),
                  purchaseEndDate: dates[1].clone().endOf("day").toDate()
                }));
              } else {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  purchaseStartDate: null,
                  purchaseEndDate: null
                }));
              }
            }}
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={8}>
          <Text strong>Activity Date filter:</Text>
          <DatePicker.RangePicker
            style={{ width: "100%", marginTop: "8px" }}
            value={
              filter.startDate && filter.endDate
                ? [dayjs(filter.startDate), dayjs(filter.endDate)]
                : []
            }
            disabledDate={(current) => current && current < dayjs("2025-06-30").startOf("day")}
            onChange={(dates) => {
              if (dates) {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  startDate: dates[0].clone().startOf("day").toDate(),
                  endDate: dates[1].clone().endOf("day").toDate()
                }));
              } else {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  startDate: null,
                  endDate: null
                }));
              }
            }}
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={8}>
          <Text strong>Courses:</Text>
          <Select
            style={{ width: "100%" }}
            placeholder="Self-paced Level 1"
            allowClear
            onChange={(value) => {
              const selectedCourse = coursesList.find((course) => course.id === value);
              if (selectedCourse) {
                handleCoursesChange(selectedCourse);
              }
            }}
          >
            {coursesList.map((course) => (
              <Option key={course.id} value={course.id}>
                {course.label}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Row
        gutter={[16, 16]}
        style={{
          marginTop: "24px",
          flexWrap: "wrap"
        }}
        justify="start"
        align="stretch"
      >
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Card style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Text strong>*Data Depands on Purchase date filter</Text>
            <Statistic
              title="Codeforces Verified Users"
              value={`${courseStats.totalCodeforcesVerifiedUsers || 0} / ${
                courseStats.totalUsers || 0
              }`}
              style={{ marginBottom: "16px" }}
            />
            <div style={{ marginTop: "auto", display: "flex", gap: "8px" }}>
              <Select
                style={{ flex: 1 }}
                value={codeforcesVerifiedFilter}
                onChange={setCodeforcesVerifiedFilter}
                size="small"
              >
                <Option value="all">All Users</Option>
                <Option value="true">Verified Only</Option>
                <Option value="false">Non Verified Only</Option>
              </Select>
              <Button
                type="primary"
                size="small"
                onClick={() => handleDownloadCodeforcesUsersCSV(codeforcesVerifiedFilter)}
              >
                Download CSV
              </Button>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Card style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Text strong>*Data Depands on Purchase date filter</Text>
            <Statistic
              title="Discord Connected Users"
              value={`${courseStats.totalDiscordConnectedUsers || 0} / ${
                courseStats.totalUsers || 0
              }`}
              style={{ marginBottom: "16px" }}
            />
            <div style={{ marginTop: "auto", display: "flex", gap: "8px" }}>
              <Select
                style={{ flex: 1 }}
                value={discordConnectedFilter}
                onChange={setDiscordConnectedFilter}
                size="small"
              >
                <Option value="all">All Users</Option>
                <Option value="true">Connected Only</Option>
                <Option value="false">Non Connected Only</Option>
              </Select>
              <Button
                type="primary"
                size="small"
                onClick={() => handleDownloadDiscordUsersCSV(discordConnectedFilter)}
              >
                Download CSV
              </Button>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Card style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Text strong>*Data Depands on Purchase date filter</Text>
            <Statistic
              title="Active Doubt Support Users"
              value={`${courseStats.totalActiveDoubtSupportUsers || 0} / ${
                courseStats.totalUsers || 0
              }`}
              style={{ marginBottom: "16px" }}
            />
          </Card>
        </Col>
      </Row>

      <Text strong>
        *Data like Unique Page visits, Problem, Lectures marked, etc before 30th June 2025 is not
        available.
      </Text>
      <Title level={3}>User Course Activity Aggregate Stats</Title>
      <Text strong>
        *Students who purchased the course based on filter and performed below activities {">"}= 1
        time in the time selected from the filter / Students who purchased the course based on
        purchase filter.
      </Text>
      <Row gutter={[16, 16]} style={{ marginTop: "24px" }}>
        <Col xs={24} lg={12}>
          <Card>
            <Statistic
              title="Visitors"
              value={`${aggregatedStats.visitors || 0} / ${aggregatedStats.totalUsers || 0}`}
              suffix={infoComponentForStatsCalc("visits course page")}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card>
            <Statistic
              title="Problem Solvers"
              value={`${aggregatedStats.problemSolvers || 0} / ${aggregatedStats.totalUsers || 0}`}
              suffix={infoComponentForStatsCalc("marks problem")}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card>
            <Statistic
              title="Lecture Markers"
              value={`${aggregatedStats.lectureMarkers || 0} / ${aggregatedStats.totalUsers || 0}`}
              suffix={infoComponentForStatsCalc("marks lecture")}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card>
            <Statistic
              title="Doubt Askers"
              value={`${aggregatedStats.doubtAskers || 0} / ${aggregatedStats.totalUsers || 0}`}
              suffix={infoComponentForStatsCalc("asks doubt")}
            />
          </Card>
        </Col>
      </Row>

      <div style={{ marginTop: "20px", display: "flex", flexDirection: "column" }}>
        <Title level={3}>Days Since Last visit Graph</Title>
        <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
          <p>
            This graph shows the total number of users who does not visit the learning page since{" "}
            last <b>X</b> days.
          </p>
          <p>
            📉 If a user hasn’t visited for X days, they’re also counted in X−1, X−2, ..., 1 day.
            That’s why the graph shows a decreasing trend
          </p>
          <div
            style={{
              marginBottom: "10px",
              display: "flex",
              flexDirection: "column",
              height: "300px",
              gap: "5px"
            }}
          >
            <AnalyticsGenericBarGraph
              dataPoints={daysSinceLastVisitData}
              xAxisFieldName="day"
              yAxisFieldName="value"
            />
          </div>
        </div>
      </div>

      <div style={{ marginTop: "20px", display: "flex", flexDirection: "column" }}>
        <Title level={3}>User Course Progress Graph</Title>
        <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
          <p>
            This graph shows the percentage of users (y-axis) who have achieved <b>X%</b> of overall
            course progress. For each user, <b>X%</b> is calculated as (Lectures Completed +
            Problems Solved) ÷ (Total Lectures + Total Problems) in the course.
          </p>
          <div
            style={{
              marginBottom: "10px",
              display: "flex",
              flexDirection: "column",
              height: "300px",
              gap: "5px"
            }}
          >
            <AnalyticsGenericBarGraph
              dataPoints={courseProgressData}
              xAxisFieldName="percentage"
              yAxisFieldName="value"
            />
          </div>
        </div>
      </div>

      <div style={{ marginTop: "20px", display: "flex", flexDirection: "column" }}>
        <Title level={3}>User Course Activity Graphs</Title>
        <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
          <Col xs={24} lg={12}>
            <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
              <h3>Daily Unique Visits Count</h3>
              <p>
                This graph shows the total number of unique visits to the learnings page, aggregated
                by date. The visit count includes events such as page loads and refreshes by the{" "}
                <b>users.</b>
              </p>
              <div
                style={{
                  marginBottom: "10px",
                  display: "flex",
                  flexDirection: "column",
                  height: "300px",
                  gap: "5px"
                }}
              >
                <AnalyticsGenericBarGraph
                  dataPoints={dailyUniqueVisitCountData}
                  xAxisFieldName="date"
                  yAxisFieldName="value"
                />
              </div>
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
              <h3>Daily Problems Marked</h3>
              <p>
                This graph shows the total number of problems marked on the learnings page,
                aggregated by date. The daily problem marks include events where <b>users</b> mark
                task problems.
              </p>
              <div
                style={{
                  marginBottom: "10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                  height: "300px"
                }}
              >
                <AnalyticsGenericBarGraph
                  dataPoints={daliyProblemsSolvedData}
                  xAxisFieldName="date"
                  yAxisFieldName="value"
                />
              </div>
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
              <h3>Daily Lectures Marked</h3>
              <p>
                This graph shows the total number of lectures marked on the learnings page,
                aggregated by date. The daily lecture marks include events where <b>users</b> mark
                lectures as completed.
              </p>
              <div
                style={{
                  marginBottom: "10px",
                  display: "flex",
                  flexDirection: "column",
                  height: "300px",
                  gap: "5px"
                }}
              >
                <AnalyticsGenericBarGraph
                  dataPoints={dailyLecturesMarkedData}
                  xAxisFieldName="date"
                  yAxisFieldName="value"
                />
              </div>
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
              <h3>Daily Doubt Created</h3>
              <p>
                This graph shows the total number of doubts created by <b>users</b> on the learnings
                page, aggregated by date. The Daily Doubts created events like creating query
                through website on discord.
              </p>
              <div
                style={{
                  marginBottom: "10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                  height: "300px"
                }}
              >
                <AnalyticsGenericBarGraph
                  dataPoints={dailyDoubtsCreatedData}
                  xAxisFieldName="date"
                  yAxisFieldName="value"
                />
              </div>
            </div>
          </Col>
        </Row>
        <div>
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            <Input
              style={{ width: "calc(100% - 150px)", minWidth: "min(400px, 100%)" }}
              size="large"
              placeholder="Filter Table on User Email ID"
              onChange={(e) => setFilterUserEmail(e.target.value)}
            />
            <Button
              type="primary"
              style={{ width: "min(150px, 100%)" }}
              onClick={fetchUserActivityTableData}
              size="large"
            >
              Search
            </Button>
          </div>
          <Row
            gutter={[16, 16]}
            style={{
              margin: "10px 0",
              border: "1px solid #d9d9d9",
              padding: "10px",
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <Col xs={24} sm={24} md={12} lg={8}>
              <Text strong style={{ fontSize: "20px" }}>
                User Course Activity Table
              </Text>
            </Col>
            <CSVLink
              asyncOnClick={true}
              data={courseRemindButtonCsvData || []}
              ref={csvLinkRef}
              filename={downloadedFileName}
            />
            <Button
              type="primary"
              onClick={() => {
                handleDownloadCsv();
              }}
            >
              Download CSV
            </Button>
          </Row>
          <Table
            columns={columns}
            dataSource={userActivityTableData}
            bordered
            pagination={true}
            scroll={{ x: "max-content" }}
          />
        </div>
      </div>
    </div>
  );
};

export default CourseAnalyticsDash;
